/**
 * Memory tools — let the planner persist or remove durable couple-level notes
 * in the per-wedding scratchpad. Notes loaded at session start are surfaced
 * inside the system prompt; new writes here persist immediately but only
 * take effect in subsequent chat sessions (Gemini system prompts are fixed
 * per session).
 */

import { Schema } from "firebase/ai";
import {
  PLANNER_MEMORY_BUCKETS,
  PLANNER_MEMORY_NOTE_MAX_LEN,
  type PlannerMemoryBucket,
} from "@amazing-wedding/shared-types";
import type { PlannerTool } from "./registry";
import { asEnum, asString, requireString } from "./helpers";

const CONFIDENCE_VALUES = ["high", "low"] as const;

const BUCKET_LABEL_PL: Record<PlannerMemoryBucket, string> = {
  preferences: "preferencja",
  decisions: "decyzja",
  followUps: "follow-up",
  facts: "fakt",
};

export const memoryTools: PlannerTool[] = [
  {
    declaration: {
      name: "remember_note",
      description:
        "Save a short note for the planner to remember across future chat sessions. Use ONLY for durable couple-level context: stated preferences, firm decisions, follow-ups they want tracked, or stable facts about their wedding. Do NOT use for transient or actionable to-dos — call create_task instead.",
      parameters: Schema.object({
        properties: {
          bucket: Schema.enumString({
            enum: [...PLANNER_MEMORY_BUCKETS],
            description:
              "preferences for stated likes/dislikes, decisions for firm choices, followUps for things they want reminded about, facts for stable wedding facts (officiant, parish, dietary constraints).",
          }),
          text: Schema.string({
            description: `Single short sentence in the couple's own language, max ${PLANNER_MEMORY_NOTE_MAX_LEN} characters.`,
          }),
          confidence: Schema.enumString({
            enum: [...CONFIDENCE_VALUES],
            description:
              "high when the couple stated it explicitly; low when you inferred it. Defaults to low.",
          }),
        },
        optionalProperties: ["confidence"],
      }),
    },
    destructive: false,
    describe: (args) => {
      const bucket = asEnum(args.bucket, PLANNER_MEMORY_BUCKETS) ?? "preferences";
      const text = asString(args.text) ?? "?";
      return `Zapamiętuję (${BUCKET_LABEL_PL[bucket]}): ${text}`;
    },
    execute: async (args, ctx) => {
      const bucket = asEnum(args.bucket, PLANNER_MEMORY_BUCKETS);
      if (!bucket) throw new Error("A valid 'bucket' is required.");
      const text = requireString(args, "text");
      const confidence = asEnum(args.confidence, CONFIDENCE_VALUES);
      const note = await ctx.operations.memory.addMemoryNote(ctx.weddingId, {
        bucket,
        text,
        source: "planner",
        confidence: confidence ?? "low",
      });
      return { ok: true, noteId: note.id, bucket, text: note.text };
    },
  },
  {
    declaration: {
      name: "forget_note",
      description:
        "Remove a previously remembered note by its id. Use only when the couple explicitly asks to forget something they had stated.",
      parameters: Schema.object({
        properties: {
          noteId: Schema.string({
            description:
              "Exact id of the note to delete, as previously returned by remember_note or listed in the system prompt.",
          }),
        },
      }),
    },
    destructive: true,
    describe: (args) => `Usunięcie notatki: ${asString(args.noteId) ?? "?"}`,
    execute: async (args, ctx) => {
      const noteId = requireString(args, "noteId");
      const removed = await ctx.operations.memory.removeMemoryNote(ctx.weddingId, noteId);
      if (!removed) {
        throw new Error(`No note with id "${noteId}" found in memory.`);
      }
      return { ok: true, noteId };
    },
  },
];
